import {REQUEST_TYPE} from '../../../constants';
import {doRequest} from '../../../helpers/network';
import {Finance} from '../../../helpers/Urls';

const INITIAL_STATE = {
  npcConfig: null,
  npcConfigLoading: false,
  npcConfigError: null,
  npcConfigSaving: false,
  npcConfigSaveError: null,
};

export const NPC_CONFIG_LOADING = 'NPC_CONFIG_LOADING';
export const FETCH_NPC_CONFIG_COMPLETED = 'FETCH_NPC_CONFIG_COMPLETED';
export const NPC_CONFIG_ERROR = 'NPC_CONFIG_ERROR';
export const SAVE_NPC_CONFIG_LOADING = 'SAVE_NPC_CONFIG_LOADING';
export const SAVE_NPC_CONFIG_COMPLETED = 'SAVE_NPC_CONFIG_COMPLETED';
export const SAVE_NPC_CONFIG_ERROR = 'SAVE_NPC_CONFIG_ERROR';

// Fetch NPC slab-rate configuration (GET /finance/npc-config/) on mount.
export const fetchNpcConfig = () => async (dispatch) => {
  dispatch({type: NPC_CONFIG_LOADING});

  try {
    const response = await doRequest({
      method: REQUEST_TYPE.GET,
      url: Finance.fetchNpcConfigURL(),
    });
    // Real config may be wrapped as {status, data} or be the raw object.
    const config =
      response && response.data && Array.isArray(response.data.slabs)
        ? response.data
        : response && Array.isArray(response.slabs)
        ? response
        : null;

    if (config) {
      dispatch({type: FETCH_NPC_CONFIG_COMPLETED, payload: config});
      return {success: true, data: config};
    }
    // No slabs configured yet — surface an empty config so the page shows its
    // empty state ("Add first slab") rather than any placeholder data.
    dispatch({type: FETCH_NPC_CONFIG_COMPLETED, payload: {slabs: []}});
    return {success: true, data: {slabs: []}};
  } catch (err) {
    dispatch({
      type: NPC_CONFIG_ERROR,
      error: (err && err.message) || 'Failed to fetch NPC configuration',
    });
    return {success: false, message: err && err.message};
  }
};

// Save NPC slab-rate configuration (POST /v2/finance/npc-config/)
export const saveNpcConfig = (payload) => async (dispatch) => {
  dispatch({type: SAVE_NPC_CONFIG_LOADING});

  try {
    const response = await doRequest({
      method: REQUEST_TYPE.POST,
      url: Finance.saveNpcConfigURL(),
      data: payload,
    });
    const {status, message} = response || {};

    if (status) {
      // The saved config comes back under `data`; keep a top-level fallback so
      // the `last_updated_at` timestamp is captured wherever the API places it.
      const savedConfig = {
        ...(response.data || {}),
        last_updated_at:
          response.data?.last_updated_at || response.last_updated_at,
      };
      dispatch({type: SAVE_NPC_CONFIG_COMPLETED, payload: savedConfig});
      return {success: true, data: savedConfig, message};
    } else {
      dispatch({
        type: SAVE_NPC_CONFIG_ERROR,
        error: message || 'Failed to save NPC configuration',
      });
      return {
        success: false,
        message: message || 'Failed to save NPC configuration',
      };
    }
  } catch (err) {
    dispatch({type: SAVE_NPC_CONFIG_ERROR, error: err});
    return {
      success: false,
      message:
        err?.response?.data?.message || err?.message || 'Something went wrong',
    };
  }
};

// Reducer
export default (state = INITIAL_STATE, action) => {
  const {type, payload} = action || {};

  switch (type) {
    case NPC_CONFIG_LOADING:
      return {...state, npcConfigLoading: true, npcConfigError: null};

    case FETCH_NPC_CONFIG_COMPLETED:
      return {
        ...state,
        npcConfigLoading: false,
        npcConfig: payload,
      };

    case NPC_CONFIG_ERROR:
      return {
        ...state,
        npcConfigLoading: false,
        npcConfigError: action.error,
      };

    case SAVE_NPC_CONFIG_LOADING:
      return {...state, npcConfigSaving: true, npcConfigSaveError: null};

    case SAVE_NPC_CONFIG_COMPLETED:
      // Merge the save response back in so the "Last updated" timestamp
      // (last_updated_at, returned by the POST) refreshes without a refetch.
      return {
        ...state,
        npcConfigSaving: false,
        npcConfig: {...state.npcConfig, ...payload},
      };

    case SAVE_NPC_CONFIG_ERROR:
      return {
        ...state,
        npcConfigSaving: false,
        npcConfigSaveError: action.error,
      };

    default:
      return state;
  }
};
